/**
 * Drag maths for the floor-plan editor.
 *
 * Pure, and separated from the component for one reason: this is where the
 * floor plan can be corrupted permanently.
 *
 * Coordinates are stored ONCE, in left-to-right space (D-067). The canvas
 * mirrors at render time in Arabic. So when a manager drags a table while the
 * room is mirrored, the pointer moves one way and the STORED coordinate must
 * move the other. Get that wrong and every drag in Arabic writes a
 * progressively more wrong floor plan to the database, with no error and no way
 * to tell until someone opens it in English.
 *
 *   rendered_x = W - stored_x - width        (RTL)
 *   so d(rendered_x) = -d(stored_x)
 *
 * Vertical is unaffected: mirroring is horizontal only.
 */

import type { Direction } from './direction.js';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CanvasBounds {
  width: number;
  height: number;
}

/**
 * Convert a pointer delta in SCREEN pixels to a delta in CANVAS units.
 *
 * The SVG is drawn at a fixed viewBox and scaled to fit, so a pointer moving 10
 * screen pixels is not 10 canvas units on a narrow tablet. Without this the
 * table lags behind or races ahead of the finger, which reads as a broken
 * control rather than a scaling bug.
 */
export function screenDeltaToCanvas(
  delta: { dx: number; dy: number },
  rendered: { width: number; height: number },
  canvas: CanvasBounds,
): { dx: number; dy: number } {
  const scaleX = rendered.width > 0 ? canvas.width / rendered.width : 1;
  const scaleY = rendered.height > 0 ? canvas.height / rendered.height : 1;
  return { dx: delta.dx * scaleX, dy: delta.dy * scaleY };
}

/** Round to the nearest step. `step <= 1` disables snapping. */
export function snap(value: number, step: number): number {
  if (step <= 1) return Math.round(value);
  return Math.round(value / step) * step;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export interface DragInput {
  /** The table's stored position before the drag began. */
  start: Rect;
  /** Pointer movement so far, already converted to canvas units. */
  delta: { dx: number; dy: number };
  direction: Direction;
  canvas: CanvasBounds;
  gridStep: number;
}

/**
 * Where the table should now be STORED.
 *
 * The one line that matters is the horizontal sign flip under RTL. Everything
 * else is snapping and staying on the canvas.
 */
export function dragToStoredPosition(input: DragInput): { x: number; y: number } {
  // A pointer moving toward the inline end moves the stored x the other way
  // when the room is mirrored.
  const horizontal = input.direction === 'rtl' ? -input.delta.dx : input.delta.dx;

  const x = snap(input.start.x + horizontal, input.gridStep);
  const y = snap(input.start.y + input.delta.dy, input.gridStep);

  return {
    x: clamp(x, 0, Math.max(0, input.canvas.width - input.start.width)),
    y: clamp(y, 0, Math.max(0, input.canvas.height - input.start.height)),
  };
}

/**
 * Nudge with the arrow keys.
 *
 * A manager lining up a row of tables wants them level, and a mouse will not do
 * it. The arrow keys are also the only way to use this editor without a
 * pointer at all.
 *
 * `dx` is in READING order - pressing "next" moves the table along the way the
 * page reads - so the keys behave the same in both directions.
 */
export function nudgeToStoredPosition(input: {
  start: Rect;
  dx: number;
  dy: number;
  direction: Direction;
  canvas: CanvasBounds;
  gridStep: number;
}): { x: number; y: number } {
  return dragToStoredPosition({
    start: input.start,
    delta: { dx: input.dx, dy: input.dy },
    direction: input.direction,
    canvas: input.canvas,
    gridStep: Math.max(1, input.gridStep),
  });
}

export interface ResizeInput {
  start: Rect;
  delta: { dx: number; dy: number };
  direction: Direction;
  canvas: CanvasBounds;
  gridStep: number;
  minSize?: number;
}

/**
 * Resize from the inline-end corner.
 *
 * In a mirrored room that corner is on the other side of the table, so the
 * horizontal delta flips exactly as it does for a move. The table's stored x
 * never changes here - only its width - because the handle is on the end the
 * stored origin is not.
 */
export function resizeToStoredSize(input: ResizeInput): {
  width: number;
  height: number;
} {
  const min = input.minSize ?? 30;
  const horizontal = input.direction === 'rtl' ? -input.delta.dx : input.delta.dx;

  const width = snap(input.start.width + horizontal, input.gridStep);
  const height = snap(input.start.height + input.delta.dy, input.gridStep);

  return {
    width: clamp(width, min, Math.max(min, input.canvas.width - input.start.x)),
    height: clamp(height, min, Math.max(min, input.canvas.height - input.start.y)),
  };
}

/** Normalise a rotation to 0-359, so the API's CHECK constraint always passes. */
export function normaliseRotation(degrees: number): number {
  return ((Math.round(degrees) % 360) + 360) % 360;
}

/**
 * Do two tables overlap?
 *
 * Not an error - a banquette really can sit against a wall table - so this only
 * warns. A floor plan that refuses to let a manager put two tables together is
 * a floor plan that does not match their restaurant.
 */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}
